/**
 * ManagePermissionsModal Component
 *
 * Modal for granting permissions to a role assignment.
 * Allows selecting a target resource type, actions, and typed source
 * designators, with role eligibility checks.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useGrantPermission, useRevokePermission } from '@/lib/hooks/usePermissions';
import { PermissionGrantEditor } from '@/components/permissions/PermissionGrantEditor';
import {
    formatPermissionEffectLabel,
    formatPermissionActionVerbList,
    formatPermissionResourceTypePluralLabel,
} from '@/components/permissions/PermissionGrantSummary';
import { type PermissionsGrant } from '@pillar-portal/api-types';
import { type AuthorizableAction, ResourceType, Role } from '@pillar-portal/database/dist/frontend';

function buildEmptyPermissionGrant(): PermissionsGrant {
    return {
        effect: 'allow',
        resource_type: ResourceType.ROLE_ASSIGNMENT_CREDENTIAL,
        actions: [],
        resources: [],
    };
}

interface ManagePermissionsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    roleAssignmentUuid: string;
    role: Role;
    organizationId: number | null;
    organizationUuid?: string | null;
    eligibleActions: readonly AuthorizableAction[];
    editingPermission?: PermissionsGrant | null;
    principalDisplayName?: string | null;
}

export function ManagePermissionsModal({
    open,
    onOpenChange,
    roleAssignmentUuid,
    role,
    organizationId: _organizationId,
    organizationUuid = null,
    eligibleActions,
    editingPermission = null,
    principalDisplayName = null,
}: ManagePermissionsModalProps) {
    const [draftPermission, setDraftPermission] = useState<PermissionsGrant>(
        buildEmptyPermissionGrant()
    );
    const [validationError, setValidationError] = useState<string | null>(null);

    const grantMutation = useGrantPermission();
    const revokeMutation = useRevokePermission();
    const grantResetRef = useRef(grantMutation.reset);
    const revokeResetRef = useRef(revokeMutation.reset);

    useEffect(() => {
        grantResetRef.current = grantMutation.reset;
        revokeResetRef.current = revokeMutation.reset;
    }, [grantMutation.reset, revokeMutation.reset]);

    useEffect(() => {
        if (!open) {
            setDraftPermission(buildEmptyPermissionGrant());
            setValidationError(null);
            return;
        }
        grantResetRef.current();
        revokeResetRef.current();
        setValidationError(null);
        setDraftPermission(
            editingPermission === null ? buildEmptyPermissionGrant() : editingPermission
        );
    }, [editingPermission, open, organizationUuid, roleAssignmentUuid]);

    useEffect(() => {
        if (grantMutation.isSuccess) {
            onOpenChange(false);
        }
    }, [grantMutation.isSuccess, onOpenChange]);

    const saveErrorMessage: string | null = useMemo(() => {
        if (validationError !== null) {
            return validationError;
        }
        if (grantMutation.error instanceof Error) {
            return grantMutation.error.message;
        }
        if (revokeMutation.error instanceof Error) {
            return revokeMutation.error.message;
        }
        return null;
    }, [grantMutation.error, revokeMutation.error, validationError]);

    const modalDescription: string = useMemo(() => {
        if (draftPermission.actions.length === 0) {
            return 'Select actions to grant permissions for.';
        }
        const actionVerbList = formatPermissionActionVerbList(draftPermission.actions);
        const principalLabel =
            principalDisplayName === null || principalDisplayName.trim().length === 0
                ? 'this user'
                : principalDisplayName;
        return `${formatPermissionEffectLabel(draftPermission.effect)} ${actionVerbList} permissions to ${principalLabel} (${role}) on ${formatPermissionResourceTypePluralLabel(draftPermission.resource_type)}`;
    }, [
        draftPermission.actions,
        draftPermission.effect,
        draftPermission.resource_type,
        principalDisplayName,
        role,
    ]);

    const handleSave = async (): Promise<void> => {
        if (draftPermission.actions.length === 0) {
            setValidationError('Select at least one action');
            return;
        }
        if (draftPermission.resources.length === 0) {
            setValidationError('Select at least one source designator');
            return;
        }

        setValidationError(null);

        if (editingPermission !== null) {
            try {
                await revokeMutation.mutateAsync({
                    role_assignment_uuid: roleAssignmentUuid,
                    permission: editingPermission,
                });
            } catch {
                return;
            }
        }

        grantMutation.mutate({
            role_assignment_uuid: roleAssignmentUuid,
            permission: draftPermission,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {editingPermission === null ? 'Grant Permissions' : 'Edit Permission'}
                    </DialogTitle>
                    <DialogDescription>{modalDescription}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <PermissionGrantEditor
                        value={draftPermission}
                        onChange={(nextPermission) => {
                            setDraftPermission(nextPermission);
                            setValidationError(null);
                        }}
                        eligibleActions={eligibleActions}
                        organizationUuid={organizationUuid}
                    />

                    {saveErrorMessage !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>{saveErrorMessage}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={grantMutation.isPending || revokeMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => void handleSave()}
                        disabled={grantMutation.isPending || revokeMutation.isPending}
                    >
                        {grantMutation.isPending || revokeMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
